import { useEffect, useState, type FormEvent } from 'react';
import { IMaskInput } from 'react-imask';
import type { ContactFormField } from '../../lib/types';
import type { TranslationCatalog } from '../../lib/widget-translations';
import type { WidgetLang } from '../../lib/widget-translations';
import { InternationalPhoneInput } from './international-phone-input';
import { CloseIcon } from './widget-icons';
import styles from './widget-preview.module.scss';

interface ContactFormProps {
  brandColor: string;
  forceErrors: boolean;
  resetSignal: number;
  translations: TranslationCatalog;
  lang: WidgetLang;
  fields: ContactFormField[];
  onSubmit: () => void;
  onClose?: () => void;
}

type ContactFormErrors = Record<string, string | undefined>;

export const ContactForm = ({
  brandColor,
  forceErrors,
  resetSignal,
  translations,
  lang,
  fields,
  onSubmit,
  onClose,
}: ContactFormProps) => {
  const [agreed, setAgreed] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [validities, setValidities] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setAgreed(false);
    setValues({});
    setErrors({});
    setValidities({});
  }, [resetSignal]);

  useEffect(() => setErrors({}), [translations]);

  const visibleErrors: ContactFormErrors = forceErrors
    ? fields.reduce<ContactFormErrors>(
        (result, field) => {
          if (field.required) {
            result[field.alias] = errors[field.alias] || translations.authFieldRequiredError;
          }
          return result;
        },
        {
          ...errors,
          agreed: errors.agreed || translations.authAgreementError,
        },
      )
    : errors;

  const updateValue = (alias: string, value: string) => {
    setValues((current) => ({ ...current, [alias]: value }));
    setErrors((current) => ({ ...current, [alias]: undefined }));
  };

  const hasValue = (value?: string) => Boolean(value?.trim());

  const validateField = (field: ContactFormField) => {
    const value = values[field.alias];
    if (field.required && !hasValue(value)) return translations.authFieldRequiredError;
    if (!value) return undefined;
    if (field.type === 'email' && !/^\S+@\S+\.\S+$/.test(value)) {
      return translations.authEmailError;
    }
    if (field.type === 'phone' && value.replace(/\D/g, '').length <= 10) {
      return translations.authPhoneError;
    }
    if (field.type === 'foreign_phone' && !validities[field.alias]) {
      return translations.authPhoneError;
    }
    if (field.type === 'number' && !Number.isFinite(Number(value))) {
      return translations.authNumberError;
    }
    return undefined;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: ContactFormErrors = {};
    if (!agreed) nextErrors.agreed = translations.authAgreementError;
    fields.forEach((field) => {
      nextErrors[field.alias] = validateField(field);
    });
    setErrors(nextErrors);
    if (!Object.values(nextErrors).some(Boolean)) onSubmit();
  };

  const requiredFieldsFilled = fields.every(
    (field) => !field.required || hasValue(values[field.alias]),
  );
  const canSubmit = agreed && requiredFieldsFilled;

  return (
    <form className={styles.contactForm} onSubmit={handleSubmit} noValidate>
      {onClose && (
        <div className={styles.contactFormActions}>
          <button
            type="button"
            onClick={onClose}
            aria-label={translations.contactFormCloseAriaLabel}
          >
            <CloseIcon />
          </button>
        </div>
      )}
      <div className={styles.contactFields}>
        {fields.map((field) => {
          const value = values[field.alias];
          const label = `${field.label}${field.required ? ' *' : ''}`;

          if (field.type === 'select') {
            return (
              <label key={field.alias} className={styles.customContactField}>
                <span>{label}</span>
                <select
                  value={value ?? ''}
                  aria-invalid={Boolean(visibleErrors[field.alias])}
                  onChange={(event) => updateValue(field.alias, event.target.value)}
                >
                  <option value="">
                    {field.placeholder || translations.authSelectPlaceholder}
                  </option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {visibleErrors[field.alias] && <small>{visibleErrors[field.alias]}</small>}
              </label>
            );
          }

          if (field.type === 'phone') {
            return (
              <label key={field.alias} className={styles.customContactField}>
                <span>{label}</span>
                <IMaskInput
                  mask="+{7} (000) 000-00-00"
                  value={value ?? ''}
                  inputMode="tel"
                  placeholder={field.placeholder || field.label}
                  aria-invalid={Boolean(visibleErrors[field.alias])}
                  onAccept={(maskedValue) => updateValue(field.alias, maskedValue)}
                />
                {visibleErrors[field.alias] && <small>{visibleErrors[field.alias]}</small>}
              </label>
            );
          }

          if (field.type === 'foreign_phone') {
            return (
              <label key={field.alias} className={styles.customContactField}>
                <span>{label}</span>
                <InternationalPhoneInput
                  key={`${field.alias}-${resetSignal}`}
                  value={value ?? ''}
                  lang={lang}
                  placeholder={field.placeholder || field.label}
                  ariaInvalid={Boolean(visibleErrors[field.alias])}
                  onChange={(phone, isValid) => {
                    setValidities((current) => ({ ...current, [field.alias]: isValid }));
                    updateValue(field.alias, phone);
                  }}
                />
                {visibleErrors[field.alias] && <small>{visibleErrors[field.alias]}</small>}
              </label>
            );
          }

          return (
            <label key={field.alias} className={styles.customContactField}>
              <span>{label}</span>
              <input
                type={field.type}
                inputMode={field.type === 'number' ? 'decimal' : undefined}
                value={value ?? ''}
                placeholder={field.placeholder || field.label}
                aria-invalid={Boolean(visibleErrors[field.alias])}
                onChange={(event) => updateValue(field.alias, event.target.value)}
              />
              {visibleErrors[field.alias] && <small>{visibleErrors[field.alias]}</small>}
            </label>
          );
        })}
      </div>
      <label className={styles.agreement}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(event) => {
            setAgreed(event.target.checked);
            setErrors((previous) => ({ ...previous, agreed: undefined }));
          }}
        />
        <span>{translations.authAgreementLabel}</span>
      </label>
      {visibleErrors.agreed && <small>{visibleErrors.agreed}</small>}
      <button type="submit" disabled={!canSubmit} style={{ backgroundColor: brandColor }}>
        {translations.authSubmitLabel}
      </button>
    </form>
  );
};
